import i18n from '@/i18n'
import { clearAuth } from './auth'

const BASE = '/api'

/** 上次 401 重定向的时间戳，防止并发请求多重定向 */
let lastUnauthorizedRedirect = 0

/** 单飞静默刷新：并发请求同时 401 时只触发一次 /auth/refresh（OPT-052 P2） */
let refreshInFlight: Promise<boolean> | null = null

function trySilentRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

/**
 * 将服务端错误对象翻译为当前语言的提示文本
 */
export function localizeError(
  error?: { code?: string; message?: string; details?: Record<string, unknown> } | null
): string {
  if (!error) return i18n.t('errors.default')
  const code = error.code
  if (code) {
    // 错误码为大写蛇形（INVALID_CREDENTIALS），i18n 以驼峰（invalidCredentials）为主、
    // 部分兼容下划线（package_depleted）——优先驼峰，其次小写下划线
    const camel = code.toLowerCase().replace(/_./g, (m) => m.slice(1).toUpperCase())
    const localized = i18n.t(`errors.${camel}`, { defaultValue: '' })
    if (localized) return localized
    const lower = code.toLowerCase()
    const legacy = i18n.t(`errors.${lower}`, { defaultValue: '' })
    if (legacy) return legacy
  }
  return error.message || i18n.t('errors.default')
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  // multipart 上传（FormData）时不能手动设置 Content-Type，
  // boundary 由浏览器自动生成；其余请求保持 JSON
  const isFormData = options.body instanceof FormData

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> || {}),
  }

  // 鉴权走 HttpOnly cookie（credentials: 'include'）；
  // OPT-052 P2：移除 localStorage 标记位拼 Bearer 的死代码（服务端只认 cookie，从未读 Authorization 头）
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (res.ok) return res.json()

  const body = await res.json().catch(() => ({}))
  const err = body?.error

  // 401 / token 过期 / 未登录：清除本地状态并跳转登录页（防抖 2s，避免并发请求多重跳转）
  // 注意：INVALID_CREDENTIALS（账号或密码错误）虽返回 401，但不是会话失效——
  // 不能整页刷新关闭登录弹窗，作为普通业务错误由调用方就地提示
  const isAuthError = res.status === 401
    && err?.code !== 'INVALID_CREDENTIALS'
    && (err?.code === undefined || err?.code === 'UNAUTHORIZED' || err?.code === 'TOKEN_EXPIRED')

  // OPT-052 P2：access token 缩短至 30 分钟后，401 先静默 refresh（单飞），成功则原请求重试一次
  if (isAuthError && !retried && (await trySilentRefresh())) {
    return request<T>(path, options, true)
  }

  if (isAuthError) {
    const now = Date.now()
    if (now - lastUnauthorizedRedirect > 2000) {
      lastUnauthorizedRedirect = now
      clearAuth()
      window.location.href = '/login'
    }
    throw new Error(i18n.t('errors.unauthorized'))
  }

  const message = err?.code
    ? localizeError(err)
    : i18n.t('errors.http', { status: res.status })

  const error = new Error(message) as Error & { code?: string; retryAfterSeconds?: number }
  error.code = err?.code
  // 登录锁定响应透传剩余等待秒数（RATE_LIMITED.details.retryAfterSeconds），供弹窗倒计时
  error.retryAfterSeconds =
    typeof err?.details?.retryAfterSeconds === 'number' ? err.details.retryAfterSeconds : undefined
  throw error
}

// ===== Types =====
// OPT-042：站内消息通知中心——2026-08-16 起仅记录流失预警（client_churn）
export type NotificationType = 'client_churn'
export interface NotificationItem {
  id: number
  type: NotificationType
  payload: Record<string, unknown> | null
  readAt: number | null
  createdAt: number
}
export interface AuthUser { id: number; email: string; name: string; language?: string | null; reminderLeadHours?: number; emailVerified?: boolean }
export type ClientGender = 'male' | 'female' | 'other' | 'secret'
export interface Client { id: number; name: string; email?: string | null; phone?: string | null; notes?: string | null; gender?: ClientGender | null; age?: number | null; avatarPath?: string | null; magicLinkToken: string; isActive: boolean; statusColor?: 'green' | 'yellow' | 'red'; remainingSessions?: number; packageSummary?: string | null; createdAt: number }
export interface Package { id: number; clientId: number; coachId: number; name: string; totalSessions: number; remainingSessions: number; usedSessions: number; status: 'active' | 'expired' | 'depleted' | 'frozen'; expiresAt?: number | null; notes?: string | null; createdAt: number; templateId?: number | null }
// OPT-023：课包套餐模板（不绑定学员）
export interface PackageTemplate { id: number; coachId: number; name: string; totalSessions: number; price?: number | null; createdAt: number }
export interface Appointment { id: number; coachId: number; clientId?: number | null; title?: string | null; scheduledDate: number; startTime: string; endTime: string; timezone: string; status: 'scheduled' | 'completed' | 'cancelled' | 'late_cancelled' | 'no_show'; location?: string | null; notes?: string | null; packageName?: string | null; packageRemainingSessions?: number | null }
// OPT-016：预约预设（备注/地点）
export interface AppointmentPreset { id: number; coachId: number; kind: 'note' | 'location'; text: string; createdAt: number }
// OPT-040：订阅档位（4 档框架，只开放 free/starter 两档）
export type SubscriptionTier = 'free' | 'starter' | 'pro' | 'studio'
export interface PricingPlan {
  tier: SubscriptionTier
  name: string
  tagline: string
  maxClients: number
  // OPT-040：模板/地点限额 + 可售标志
  maxTemplates: number
  maxLocations: number
  sellable: boolean
  priceMonthly: number
  features: string[]
  available: boolean[]
  comingSoon?: boolean
}
export interface CurrentSubscription {
  tier: SubscriptionTier
  maxClients: number
  maxTemplates: number
  maxLocations: number
  priceMonthly: number
  features: string[]
  available: boolean[]
  expiresAt?: number | null
  // OPT-055：后台备注（福利升级标记等）
  notes?: string | null
}
export interface UsageInfo { current: number; max: number; isOverLimit: boolean }

export const api = {
  // 通用
  localizeError,

  // Auth
  register: (body: { email: string; password: string; name: string }) =>
    request<{ user: AuthUser }>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),

  login: (body: { email: string; password: string }) =>
    request<{ user: AuthUser }>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),

  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () => request<AuthUser>('/auth/me'),

  // OPT-031：忘记密码（落地页登录弹窗用）
  forgotPassword: (email: string) => request<{ ok: boolean }>('/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),

  // OPT-032：Google 登录（返回是否启用 + 授权跳转 URL）
  googleConfig: () => request<{ enabled: boolean; authUrl?: string }>('/auth/google/config'),

  // OPT-051：注册邮箱验证
  verifyEmail: (token: string) =>
    request<{ ok: boolean; verified: boolean }>('/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),

  resendVerification: () =>
    request<{ ok: boolean; alreadyVerified?: boolean }>('/auth/resend-verification', { method: 'POST' }),

  // Clients
  getClients: () => request<{ items: Client[] }>('/clients'),

  createClient: (body: { name: string; email?: string; phone?: string; notes?: string; gender?: ClientGender | null; age?: number | null }) =>
    request<Client & { statusColor?: string; totalRemaining?: number }>('/clients', { method: 'POST', body: JSON.stringify(body) }),

  updateClient: (id: number, body: Partial<{ name: string; email?: string | null; phone?: string | null; notes?: string | null; gender?: ClientGender | null; age?: number | null; isActive?: boolean }>) =>
    request<Client & { statusColor?: string; totalRemaining?: number }>(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteClient: (id: number) =>
    request<{ ok: boolean }>(`/clients/${id}`, { method: 'DELETE' }),

  // 头像：multipart 上传（字段名 file，限 2MB jpg/png）
  uploadClientAvatar: (id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<Client>(`/clients/${id}/avatar`, { method: 'POST', body: form })
  },

  // 头像图片地址：鉴权走 HttpOnly cookie，img src 直接引用即可（浏览器自动带 cookie）
  clientAvatarUrl: (id: number) => `${BASE}/clients/${id}/avatar`,

  // Packages
  getPackages: () => request<{ items: Package[] }>('/packages'),

  // 按学员拉课包（后端支持 clientId 过滤；可用性由调用方再过滤）
  getClientPackages: (clientId: number) =>
    request<{ items: Package[] }>(`/packages?clientId=${clientId}`),

  // OPT-011：课包名必填（后端 zod min(1).max(100)）
  createPackage: (body: { clientId: number; name: string; totalSessions: number; expiresAt?: number; notes?: string; templateId?: number }) =>
    request<Package>('/packages', { method: 'POST', body: JSON.stringify(body) }),

  // OPT-023：课包套餐模板
  getPackageTemplates: () => request<{ items: PackageTemplate[] }>('/package-templates'),
  createPackageTemplate: (body: { name: string; totalSessions: number; price?: number }) =>
    request<PackageTemplate>('/package-templates', { method: 'POST', body: JSON.stringify(body) }),
  deletePackageTemplate: (id: number) =>
    request<void>(`/package-templates/${id}`, { method: 'DELETE' }),
  updatePackageTemplate: (id: number, body: { name?: string; totalSessions?: number; price?: number }) =>
    request<PackageTemplate>(`/package-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // 按学员获取课包列表
  getClientPackagesById: (clientId: number) =>
    request<{ items: Package[] }>(`/clients/${clientId}/packages`),

  // Appointments
  getAppointments: (params?: { startDate?: string; endDate?: string }) => {
    const q = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : ''
    return request<{ items: Appointment[] }>(`/appointments${q}`)
  },

  // OPT-011：预约必绑课包（clientId/packageId 均必填）
  createAppointment: (body: { clientId: number; packageId: number; title?: string; scheduledDate: number; startTime: string; endTime: string; location?: string; notes?: string }) =>
    request<{ appointment: Appointment }>('/appointments', { method: 'POST', body: JSON.stringify(body) }),

  updateAppointment: (id: number, body: Partial<{ title: string; notes: string; location: string }>) =>
    request<{ appointment: Appointment }>(`/appointments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // 完成才扣课时；绑定课包剩余为 0 时仍成功，响应带 warning: 'package_depleted'
  completeAppointment: (id: number) =>
    request<{ appointment: Appointment; warning?: 'package_depleted' }>(`/appointments/${id}/complete`, { method: 'POST' }),

  // OPT-013：教练直接改期（立即生效），日历拖拽与面板"更改时间"统一入口
  moveAppointment: (id: number, body: { scheduledDate: number; startTime: string; endTime: string }) =>
    request<Appointment>(`/appointments/${id}/move`, { method: 'POST', body: JSON.stringify(body) }),

  // OPT-028：时间冲突检测
  checkConflicts: (params: { date: string; startTime: string; endTime: string; excludeId?: number }) => {
    const qs = new URLSearchParams(params as any).toString()
    return request<{ items: { id: number; clientName: string | null; startTime: string; endTime: string; location: string | null }[] }>(`/appointments/conflicts?${qs}`)
  },

  deleteAppointment: (id: number) =>
    request<{ ok: boolean }>(`/appointments/${id}`, { method: 'DELETE' }),

  // no-show 路由直接返回预约对象（非 { appointment } 包装）
  noShowAppointment: (id: number) =>
    request<Appointment>(`/appointments/${id}/no-show`, { method: 'POST' }),

  initiateReschedule: (id: number, body: { newDate: string; newStartTime: string; newEndTime: string }) =>
    request<{ record: unknown; confirmationUrl: string }>(`/appointments/${id}/reschedule`, { method: 'POST', body: JSON.stringify(body) }),

  // OPT-016：预约预设（备注/地点）
  getAppointmentPresets: (kind: 'note' | 'location') =>
    request<{ items: AppointmentPreset[] }>(`/appointment-presets?kind=${kind}`),

  createAppointmentPreset: (kind: 'note' | 'location', text: string) =>
    request<AppointmentPreset>('/appointment-presets', { method: 'POST', body: JSON.stringify({ kind, text }) }),

  deleteAppointmentPreset: (id: number) =>
    request<void>(`/appointment-presets/${id}`, { method: 'DELETE' }),

  // OPT-034：订阅
  getSubscriptionCurrent: () => request<CurrentSubscription>('/subscription/current'),
  getSubscriptionUsage: () => request<UsageInfo>('/subscription/usage'),
  getSubscriptionPricing: () => request<PricingPlan[]>('/subscription/pricing'),
  // OPT-040：升级仅限可售两档（free/starter）
  upgradeSubscription: (tier: 'free' | 'starter') =>
    request<{ tier: string; maxClients: number }>('/subscription/upgrade', { method: 'POST', body: JSON.stringify({ tier }) }),

  // OPT-040：更新个人资料（语言 / 预约提醒提前时长）
  updateMe: (body: { name?: string; language?: string; reminderLeadHours?: number }) =>
    request<AuthUser>('/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),

  // OPT-043：发送测试邮件（设置页邮件测试，验证邮件服务连通性）
  sendTestEmail: (email: string) =>
    request<{ sent: boolean; reason?: string }>('/email/test', { method: 'POST', body: JSON.stringify({ email }) }),

  // OPT-042：站内消息通知中心
  getNotifications: (params?: { cursor?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.cursor) qs.set('cursor', String(params.cursor))
    if (params?.limit) qs.set('limit', String(params.limit))
    const q = qs.toString()
    return request<{ items: NotificationItem[]; nextCursor: number | null; unread: number }>(`/notifications${q ? `?${q}` : ''}`)
  },
  getNotificationUnread: () => request<{ count: number }>('/notifications/unread-count'),
  markNotificationsRead: () => request<{ ok: boolean }>('/notifications/read-all', { method: 'POST' }),
  deleteNotification: (id: number) => request<void>(`/notifications/${id}`, { method: 'DELETE' }),
  clearNotifications: () => request<void>('/notifications', { method: 'DELETE' }),
}

/** OPT-040：下载单个学员 GDPR 导出 CSV（走 blob，非 JSON；鉴权走 HttpOnly cookie） */
export async function downloadClientCsv(clientId: number): Promise<void> {
  const res = await fetch(`${BASE}/clients/${clientId}/export`, {
    credentials: 'include',
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(localizeError(body?.error))
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `client-${clientId}-export.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
